// Adaptations — per-run leveled abilities drawn from growth-spurt cards
// (amoeba-unification.md). ONE pool: the former shop skills live here now
// (dash is innate and appears nowhere). Which adaptations exist in the card
// pool is a function of evolution tier — no purchases, no unlock state.
//
// Chunky by design: 3 fat levels each; every card pick should feel like a
// gear change (VS lesson). Levels live on the player entity and die with it.

import type { Entity } from './components'
import { T } from './tunables'

export type PowerId =
  | 'osmotic-reach' | 'endosymbiont' | 'axopod' | 'sporulation' | 'galvanism'
  | 'flagellate' | 'autophagy' | 'hypertrophy' | 'membrane-lock'

export interface PowerDef {
  id: PowerId
  /** Voice-checked (amoeba-voice): plausibly-academic biology noun. */
  name: string
  /** Card flavor. Deadpan, ≤ 10 words. */
  flavor: string
  /** Dry mechanical description (states the effect + control). */
  description: string
  kind: 'passive' | 'aimed' | 'active'
  maxLevel: number
  /** Evolution tier at which this joins the card pool. */
  tierRequired: number
  /** Card tint + HUD chip color. */
  tint: [number, number, number]
  /** Single-letter fallback glyph (chips render SVG icons — ui/skill-icons.ts). */
  glyph: string
}

export const POWER_DEFS: Record<PowerId, PowerDef> = {
  'osmotic-reach': {
    id: 'osmotic-reach',
    name: 'Osmotic Reach',
    flavor: 'The membrane extends its jurisdiction.',
    description: 'Eat range +35% of body radius per level.',
    kind: 'passive',
    maxLevel: 3,
    tierRequired: 1,
    tint: [1.0, 0.85, 0.45],
    glyph: 'O',
  },
  'endosymbiont': {
    id: 'endosymbiont',
    name: 'Endosymbiont',
    flavor: 'A smaller cell has moved in. It seems helpful.',
    description: 'Automatic. One orbiting helper per level; bites and grazes.',
    kind: 'passive',
    maxLevel: 3,
    tierRequired: 1,
    tint: [0.5, 0.9, 0.75],
    glyph: 'E',
  },
  // Converted from owning the click (A1, monorail-g79): LMB is the innate
  // weapon attack now; Axopod boosts it — the first power×gear synergy.
  'axopod': {
    id: 'axopod',
    name: 'Axopod',
    flavor: 'The body extends a firm opinion.',
    description: 'Attack damage +25% and reach +20% per level, any weapon.',
    kind: 'passive',
    maxLevel: 3,
    tierRequired: 1,
    tint: [1.0, 0.55, 0.4],
    glyph: 'A',
  },
  'flagellate': {
    id: 'flagellate',
    name: 'Flagellate',
    flavor: 'Somewhere to be, suddenly.',
    description: 'Move speed +16% per level.',
    kind: 'passive',
    maxLevel: 3,
    tierRequired: 1,
    tint: [0.65, 0.8, 1.0],
    glyph: 'F',
  },
  'autophagy': {
    id: 'autophagy',
    name: 'Autophagy',
    flavor: 'The body eats its problems. Including damage.',
    description: 'Regenerate 0.45 HP/s per level.',
    kind: 'passive',
    maxLevel: 3,
    tierRequired: 1,
    tint: [0.75, 0.95, 0.6],
    glyph: 'H',
  },
  'hypertrophy': {
    id: 'hypertrophy',
    name: 'Hypertrophy',
    flavor: 'More of you. The committee approves.',
    description: 'Body +13% per level. F: engulf swell absorbs predators.',
    kind: 'active',
    maxLevel: 3,
    tierRequired: 2,
    tint: [0.95, 0.8, 0.55],
    glyph: 'Y',
  },
  'membrane-lock': {
    id: 'membrane-lock',
    name: 'Membrane Lock',
    flavor: 'Briefly, a wall. Politely, a fortress.',
    description: 'SHIFT: invincibility. Longer and sooner per level.',
    kind: 'active',
    maxLevel: 3,
    tierRequired: 2,
    tint: [0.7, 0.85, 0.95],
    glyph: 'M',
  },
  'sporulation': {
    id: 'sporulation',
    name: 'Sporulation',
    flavor: 'Surplus self, packaged and mailed at problems.',
    description: 'Automatic. Spores shrink nearby predators toward edible.',
    kind: 'passive',
    maxLevel: 3,
    tierRequired: 2,
    tint: [0.75, 0.85, 0.5],
    glyph: 'S',
  },
  'galvanism': {
    id: 'galvanism',
    name: 'Galvanism',
    flavor: 'Static builds up. It has to go somewhere.',
    description: 'Automatic. Periodic zap chains through one predator per level.',
    kind: 'passive',
    maxLevel: 3,
    tierRequired: 3,
    tint: [0.6, 0.85, 1.0],
    glyph: 'Z',
  },
}

export const ALL_POWER_IDS = Object.keys(POWER_DEFS) as PowerId[]

/** The card pool at a given evolution tier — no unlock state, pure function. */
export function poolAtTier(tier: number): PowerId[] {
  return ALL_POWER_IDS.filter(id => POWER_DEFS[id].tierRequired <= tier)
}

// ── Per-run levels (on the player entity; reset by respawn's fresh entity) ──

export function powerLevel(player: Entity, id: PowerId): number {
  return player.powers?.levels[id] ?? 0
}

/** Grant one level (capped). Returns the new level, or null if already maxed. */
export function grantPowerLevel(player: Entity, id: PowerId): number | null {
  player.powers ??= { levels: {} }
  const cur = player.powers.levels[id] ?? 0
  if (cur >= POWER_DEFS[id].maxLevel) return null
  const next = cur + 1
  player.powers.levels[id] = next
  return next
}

// ── Stat accessors (identity when unleveled) ────────────────────────────────

/** Extra eat-reach in world units from Osmotic Reach (fraction of body radius). */
export function osmoticReachBonus(player: Entity): number {
  const level = powerLevel(player, 'osmotic-reach')
  if (level === 0) return 0
  const pr = player.renderable?.radius ?? T.player.baseRadius
  return pr * T.powers.osmoticReach.reachFracPerLevel * level
}

/** Move-speed multiplier from Flagellate. */
export function flagellateSpeedMult(player: Entity): number {
  return 1 + T.powers.flagellate.speedAddPerLevel * powerLevel(player, 'flagellate')
}

/** HP/s from Autophagy (0 when unleveled). */
export function autophagyRate(player: Entity): number {
  return T.powers.autophagy.ratePerLevel * powerLevel(player, 'autophagy')
}

/** Body-radius multiplier from Hypertrophy. */
export function hypertrophyRadiusMult(player: Entity): number {
  return 1 + T.powers.hypertrophy.radiusAddPerLevel * powerLevel(player, 'hypertrophy')
}

/** Attack-damage multiplier from Axopod (applies to any equipped weapon). */
export function axopodDamageMult(player: Entity): number {
  return 1 + T.powers.axopod.damageMultPerLevel * powerLevel(player, 'axopod')
}

/** Attack-reach multiplier from Axopod (melee length / ranged flight time). */
export function axopodReachMult(player: Entity): number {
  return 1 + T.powers.axopod.reachMultPerLevel * powerLevel(player, 'axopod')
}
